import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useCart();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/products', label: 'Products' },
    { href: '/warranty', label: 'Warranty' },
    { href: '/returns', label: 'Returns' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleNavClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#222222]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo - Increased size */}
          <Link to="/" onClick={handleNavClick} className="flex items-center" data-testid="header-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_ppf-kit-customizer/artifacts/aut6oyje_PHOTO-2026-02-07-23-08-24.jpg"
              alt="DIY PPF Kit"
              className="h-14 md:h-16 lg:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={handleNavClick}
                className={`nav-link text-sm font-medium tracking-wide ${isActive(link.href) ? 'active' : ''}`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side - CTA + Cart */}
          <div className="flex items-center space-x-4">
            <Link to="/products" onClick={handleNavClick} className="hidden md:block">
              <Button 
                className="bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] px-6 py-2 rounded-none"
                data-testid="build-kit-btn"
              >
                Build Kit
              </Button>
            </Link>
            
            <Link to="/checkout" onClick={handleNavClick} className="relative" data-testid="cart-icon">
              <ShoppingCart className="w-6 h-6 text-[#A0A0A0] hover:text-[#F8F8F8] transition-colors" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#00F0FF] rounded-full flex items-center justify-center text-[10px] font-bold text-[#050505]">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#F8F8F8]" />
              ) : (
                <Menu className="w-6 h-6 text-[#F8F8F8]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-[#222222]">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link text-base font-medium py-2 ${isActive(link.href) ? 'active' : ''}`}
                  onClick={handleNavClick}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/products" onClick={handleNavClick}>
                <Button 
                  className="w-full bg-[#00F0FF] text-[#050505] font-bold uppercase tracking-wide hover:bg-[#33F3FF] py-3 rounded-none"
                  data-testid="mobile-build-kit-btn"
                >
                  Build Kit
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
